// Global Instructions Rule Applied!
//
// Rounds HTTP calls (typed). Create: POST /rounds (standalone round — owner
// decision 2026-08-26: a round's only required FK is its company; deals link
// via deals.round). Detail: GET /rounds/:id, the full form's cold-load path.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { RoundEntry, validateRoundEntry } from '../../../state_manager/data_models/round_entry.ts'
import {
  RoundDeleteImpact,
  validateRoundDeleteImpact
} from '../../../state_manager/data_models/round_delete_impact.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
})

export interface CreateRoundPayload {
  account: string
  name: string
  funding_stage?: string | null
  security_type?: string | null
  currency?: string | null
  round_close_date?: string | null
  raise_amount?: number | null
  pre_money_valuation?: number | null
  post_money_valuation?: number | null
}

/** Creates a standalone round; resolves with the new round's RoundEntry-shaped
 *  fields (rounds__*), ready for `addRound` into the rounds slice. */
export const createRound = async (
  accessToken: string | null,
  payload: CreateRoundPayload
): Promise<{ data: RoundEntry | null; roundId?: string; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ROUNDS.CREATE), {
      method: API_ROUTES.ROUNDS.CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Round create failed (${response.status})`)
    }
    const body = await response.json()
    const roundId = body?.data?.search_value as string | undefined
    if (!roundId) throw new Error('Round create returned no id')
    const round = validateRoundEntry({ ...(body?.data?.data || {}) })
    return { data: round, roundId }
  } catch (e) {
    console.error('createRound failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

/** One round (RoundEntry shape) for the full form's cold-load path. */
export const fetchRoundDetail = async (
  accessToken: string | null,
  roundId: string
): Promise<{ data: RoundEntry | null; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ROUNDS.DETAIL, { id: roundId }, {}), {
      method: API_ROUTES.ROUNDS.DETAIL.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Round request failed (${response.status})`)
    }
    const body = await response.json()
    const round = validateRoundEntry(body.data || {})
    return { data: round }
  } catch (e) {
    console.error('fetchRoundDetail failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

/** What deleting this round would touch — the preflight behind every delete
 *  confirmation, including the deal form's "Is Funding Round" untick. */
export const fetchRoundDeleteImpact = async (
  accessToken: string | null,
  roundId: string
): Promise<{ data: RoundDeleteImpact | null; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ROUNDS.DELETE_IMPACT, { id: roundId }, {}),
      {
        method: API_ROUTES.ROUNDS.DELETE_IMPACT.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Round delete impact failed (${response.status})`)
    }
    const body = await response.json()
    return { data: validateRoundDeleteImpact(body.data || {}) }
  } catch (e) {
    console.error('fetchRoundDeleteImpact failed:', e)
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

/** Soft-deletes a round: its instruments and co-investors go with it, linked
 *  deals are unlinked. Refused (422) while a cap-table version or legal
 *  agreement set still references it — the backend's sentence is the error. */
export const deleteRound = async (
  accessToken: string | null,
  roundId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.ROUNDS.DELETE, { id: roundId }, {}),
      {
        method: API_ROUTES.ROUNDS.DELETE.method,
        headers: authHeaders(accessToken)
      }
    )
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.message || body.error || `Round delete failed (${response.status})`)
    }
    return { success: true }
  } catch (e) {
    console.error('deleteRound failed:', e)
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}
